import { Settings, User, Volume2, VolumeX } from '@/components/ui/icons';
import { Popover } from '@/components/ui/Popover';
import { Switch } from '@/components/ui/Input';
import { useAuth } from '@/stores/auth';
import { useUi } from '@/stores/ui';
import { useUser } from '@/stores/users';
import { useVoiceSettings } from '@/stores/voiceSettings';

/**
 * Right-click menu for someone in a voice channel: set how loud they are for
 * you, or mute them just for yourself. Saved per device.
 */
export function VoiceUserMenu({
  userId,
  serverId,
  point,
  onClose,
}: {
  userId: string;
  serverId: string;
  point: { x: number; y: number };
  onClose: () => void;
}) {
  const user = useUser(userId);
  const isMe = useAuth((s) => s.user?.id === userId);
  const volume = useVoiceSettings((s) => s.userVolumes[userId] ?? 100);
  const muted = useVoiceSettings((s) => !!s.localMutes[userId]);
  const setUserVolume = useVoiceSettings((s) => s.setUserVolume);
  const setLocalMute = useVoiceSettings((s) => s.setLocalMute);
  const openModal = useUi((s) => s.openModal);

  return (
    <Popover open onClose={onClose} anchor={point} placement="bottom-start" label={`Voice options for ${user?.displayName ?? 'user'}`} className="w-64 p-3">
      <p className="mb-2 truncate text-sm font-bold text-fg">{user?.displayName ?? 'User'}</p>
      {isMe ? (
        <button
          type="button"
          onClick={() => {
            onClose();
            openModal({ type: 'userSettings', section: 'voice' });
          }}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-fg-soft hover:bg-hover"
        >
          <Settings size={16} /> Voice & video settings
        </button>
      ) : (
        <>
          <label className="mb-1 flex items-center justify-between text-xs font-bold tracking-wide text-fg-muted uppercase">
            User volume <span className="font-mono normal-case tabular-nums">{muted ? 'muted' : `${volume}%`}</span>
          </label>
          <div className="flex items-center gap-2">
            {volume === 0 || muted ? <VolumeX size={16} className="text-fg-muted" /> : <Volume2 size={16} className="text-fg-muted" />}
            <input
              type="range"
              min={0}
              max={100}
              value={volume}
              onChange={(e) => setUserVolume(userId, Number(e.target.value))}
              aria-label={`Volume for ${user?.displayName ?? 'user'}`}
              className="clanfire-range h-5 flex-1 cursor-pointer"
              style={{ '--fill': `${volume}%` } as React.CSSProperties}
            />
          </div>
          <div className="-mb-2">
            <Switch label="Mute for me" description="Only you stop hearing them." checked={muted} onChange={(v) => setLocalMute(userId, v)} />
          </div>
          {volume !== 100 && (
            <button type="button" onClick={() => setUserVolume(userId, 100)} className="mt-2 text-xs text-link hover:underline">
              Reset volume
            </button>
          )}
        </>
      )}
      <button
        type="button"
        onClick={() => {
          onClose();
          openModal({ type: 'profile', userId, serverId });
        }}
        className="mt-2 flex w-full items-center gap-2 rounded-lg border-t border-line px-2 pt-2.5 pb-1 text-left text-sm text-fg-soft hover:text-fg"
      >
        <User size={16} /> View profile
      </button>
    </Popover>
  );
}
